import { Observable, Subject, Subscription, EMPTY, filter, takeWhile } from 'rxjs';

import { RxQueueConfig, RxQueueEvent, RxQueueResource } from './types';

export class RxQueue<E = unknown, T extends string | number = string | number> extends Observable<
  RxQueueEvent<E, T>
> {
  protected pending: RxQueueResource<E, T>[] = [];

  protected active = new Map<T, { resource: RxQueueResource<E, T>; subscription: Subscription }>();

  protected events$ = new Subject<RxQueueEvent<E, T>>();

  protected paused = false;

  protected destroyed = false;

  constructor(protected config: RxQueueConfig = { parallel: 1 }) {
    super((subscriber) => this.events$.subscribe(subscriber));
  }

  push(resource: RxQueueResource<E, T>): Observable<RxQueueEvent<E, T>> {
    if (this.destroyed) {
      return EMPTY;
    }

    this.pending.push(resource);
    this.drain();

    return this.events$.pipe(
      filter((event) => event.resource.id === resource.id),
      takeWhile((event) => event.type === 'event', true),
    );
  }

  pop(): RxQueueResource<E, T> | undefined {
    return this.pending.pop();
  }

  removeBy(fn: (resource: RxQueueResource<E, T>) => boolean): RxQueueResource<E, T>[] {
    const removed: RxQueueResource<E, T>[] = [];

    this.pending = this.pending.filter((resource) => {
      if (fn(resource)) {
        removed.push(resource);
        return false;
      }
      return true;
    });

    return removed;
  }

  /**
   * Suspends the entire queue. Active resources are unsubscribed and
   * moved back to the front of the pending queue. Call {@link continue}
   * to resume — resources will be resubscribed from scratch.
   *
   * **Important:** resource emitters must be cold (resubscribable).
   * Hot or stateful emitters will lose in-flight progress on pause.
   */
  pause(): void {
    this.paused = true;

    const suspended = [...this.active.values()];
    this.active.forEach(({ subscription }) => subscription.unsubscribe());
    this.active.clear();

    this.pending.unshift(...suspended.map(({ resource }) => resource));
  }

  continue(): void {
    this.paused = false;
    this.drain();
  }

  destroy(): void {
    this.destroyed = true;

    this.active.forEach(({ subscription }) => subscription.unsubscribe());
    this.active.clear();

    this.pending = [];
    this.events$.complete();
  }

  protected drain(): void {
    if (this.paused || this.destroyed) {
      return;
    }

    while (this.active.size < this.config.parallel && this.pending.length > 0) {
      const resource = this.pending.shift()!;
      this.activate(resource);
    }
  }

  protected activate(resource: RxQueueResource<E, T>): void {
    const subscription = new Subscription();
    this.active.set(resource.id, { resource, subscription });
    subscription.add(
      resource.emitter.subscribe({
        next: (value) => {
          this.events$.next({ type: 'event', detail: value, resource });
        },
        error: (error) => {
          this.events$.next({ type: 'error', error, resource });
          this.deactivate(resource);
        },
        complete: () => {
          this.events$.next({ type: 'completion', resource });
          this.deactivate(resource);
        },
      }),
    );
  }

  protected deactivate(resource: RxQueueResource<E, T>): void {
    this.active.delete(resource.id);
    this.drain();
  }
}
